<template>
  <div v-if="item" class="flex flex-col xl:grid xl:grid-cols-5 gap-8 pt-8 px-4 xl:px-[15%]">
    <div class="col-span-3 contents xl:block">
      <div class="order-1">
        <div class="w-full aspect-square relative mb-4">
          <template v-if="item.attributes.images.length">
            <img :src="item.attributes.images[activeImage]" class="object-cover h-full w-full" />
            <div v-if="item.attributes.images.length > 1" class="absolute top-0 left-0 z-10 w-1/2 h-full flex items-center p-6 cursor-pointer" @click="movePreviousImage">
              <i class="fas fa-chevron-left text-white shadow-sm drop-shadow-lg text-3xl"></i>
            </div>
            <div v-if="item.attributes.images.length > 1" class="absolute top-0 right-0 z-10 w-1/2 h-full flex items-center p-6 cursor-pointer justify-end" @click="moveNextImage">
              <i class="fas fa-chevron-right text-white shadow-sm drop-shadow-lg text-3xl"></i>
            </div>
          </template>
          <template v-else>
            <div class="bg-white h-full w-full flex items-center justify-center">
              <span class="fa-stack text-[72px] opacity-20">
                <i class="fa-image far fa-stack-1x" aria-hidden="true"></i>
                <i class="fa-slash fas icon-stack-base fa-stack-1x " aria-hidden="true"></i>
              </span>
            </div>
          </template>
        </div>
        <div class="flex justify-between mb-4 xl:mb-10">
          <div>
            <div class="text-2xl font-bold mb-2">{{ activeItem.name }}</div>
            <div class="font-medium">
              {{ item.attributes.profile_name }}
            </div>
            <div class="font-medium underline text-gray-500">
              {{ item.attributes.address }}
            </div>
          </div>
          <div>
            <div v-if="item.attributes.google_rating" class="shrink-0 py-3 px-4 rounded-lg bg-gray-100">
              <i class="bi bi-star"></i>
              <span class="font-bold">{{ item.attributes.google_rating }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="order-3">
        <div class="mb-4 gap-4 flex items-center">
          <div class="font-bold text-lg">
            {{ i18n.t('marketplace.items_show.about') }}
          </div>
          <button 
            v-if="selectedVariant && selectedVariant.translated_description_url" 
            @click="showTranslation = !showTranslation" 
            class="ml-auto text-xs font-bold cursor-pointer text-blue-600 hover:underline">
            {{ showTranslation ? i18n.t('marketplace.items_show.show_original') : i18n.t('marketplace.items_show.translate') }}
          </button>
        </div>
        <div v-if="!showTranslation" v-html="activeItem.description" class="mb-10 whitespace-pre-line"></div>
        <div v-else-if="descriptionTranslation" v-html="descriptionTranslation" class="mb-10 whitespace-pre-line"></div>
        <div v-else class="mb-10 flex justify-center">
          <span class="text-sm opacity-50">{{ i18n.t('marketplace.items_show.loading_translation') }}</span>
        </div>
        <hr class="mb-10"></hr>

        <div v-if="hasReviews" class="mb-4 gap-4 text-sm cursor-pointer flex" @click="showReviews = true">
          <div class="font-bold text-lg mb-2">
            {{ i18n.t('marketplace.items_show.reviews') }}
          </div>
          <div class="ml-auto">
            <span class="text-xs font-bold cursor-pointer text-blue-600">{{ i18n.t('item.show_reviews') }}</span><br>
            <span class="opacity-50 text-xs text-jt-dark-grey">Powered by Google</span>
          </div>
        </div>
        <div v-if="showReviews" class="flex flex-col gap-2 mb-4 pr-16">
          <div v-for="(review, index) in reviews" :key="index" class="flex flex-col gap-2 mb-2 p-2 px-3 rounded bg-white">
            <div class="flex items-center gap-4">
              <img :src="review.profile_photo_url" class="w-8 h-8 rounded-lg"/>
              <div class="flex flex-col">
                <span class="font-bold text-sm">{{ review.author_name }}</span>
                <span class="text-xs opacity-50">{{ review.relative_time_description }}</span>
              </div>
            </div>
            <div>
              <div class="flex items-center gap-1 mb-1">
                <i v-for="n in review.rating" :key="n" class="bi bi-star"></i>
              </div>
              <span class="text-sm">{{ review.text }}</span>
            </div>
          </div>
          <div v-if="reviewsNotLoaded" class="flex justify-center">
            <span class="text-sm opacity-50">{{ i18n.t('item.loading_reviews') }}</span>
          </div>
          <div v-else-if="reviews.length == 0" class="flex justify-center">
            <span class="text-sm opacity-50">{{ i18n.t('item.no_reviews') }}</span>
          </div>
        </div>
      </div>
    </div>  
    <div v-if="item && selectedVariant" class="col-span-2 order-2">
      <div class="flex flex-col" v-if="variants.length >= 1">
        <div class="mb-10">
          <div class="font-bold text-lg mb-2">
            {{ i18n.t('marketplace.items_show.variant') }}
          </div>
          <div v-for="variant in variants" :key="variant.id" @click="selectedVariant=variant" 
              class="cursor-pointer flex items-center gap-2 py-3 px-4 mb-2 rounded-lg bg-gray-100 hover:bg-gray-200 border-2 border-solid"
              :class="{
                '!border-black': selectedVariant && selectedVariant.id === variant.id,
                'border-transparent': !selectedVariant || selectedVariant.id !== variant.id
              }">
            <span class="font-semibold">{{ variant.duration || variant.size }}</span>
            -
            {{ variant.name }}
          </div>
        </div>
        <div class="mb-10">
          <div class="font-bold text-lg mb-2">
            {{ i18n.t('marketplace.items_show.pick_up_and_return') }}
          </div>
          <ItemCalendar v-if="item && !cartExists && !itemAddedToCart" :cart="cart" :minimumRental="item.attributes.minimum_rental" :key="selectedVariant.id" :availabilityUrl="selectedVariant.availability" :isLesson="item.attributes.itype == 1" @updateDates="updateDates" />
          <div v-else-if="cart" class="py-2 font-semibold">
            <div class="py-3 px-4 mb-2 rounded-lg bg-gray-100">
              <div class="flex items-center gap-2">
                <label v-if="isLesson" class="inline-block w-20">
                  {{ i18n.t('marketplace.calendar.start_time') }}
                </label>
                <label v-else  class="inline-block w-20">
                  {{ i18n.t('marketplace.calendar.pick_up_time') }}
                </label>
                {{ cart.start_date }}
              </div>
            </div>
          
            <template v-if="!isLesson">
              <div class="py-3 px-4 mb-2 rounded-lg bg-gray-100 flex items-center gap-2">
                <label  class="inline-block w-20">
                  {{ i18n.t('marketplace.calendar.return_time') }}
                </label>
                {{ cart.end_date }}
              </div>
            </template>
          </div>
        </div>
        <div v-if="false && item.attributes.delivery && !isLesson" class="mb-10">
          <div class="mb-6 flex items-center justify-between">
            <div class="font-bold text-xl">
              {{ i18n.t('marketplace.general.delivery') }}
            </div>
            <div>
              <input type="checkbox" class="jt-checkbox-v2" v-model="with_transport" id="delivery-checkbox" />
              <label for="delivery-checkbox" class="jt-checkbox-v2"></label>
            </div>
          </div>
          <div v-if="with_transport" class="mb-6 flex items-start gap-4">
            <div class="grow">
              <div class="text-xs mb-2">
                {{ item.attributes.delivery.description }}
                {{ item.attributes.delivery.area }}
                {{ item.attributes.delivery.price }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="false && item.attributes.transport && isLesson" class="mb-10">
          <div class="mb-6 flex items-center justify-between">
            <div class="font-bold text-xl">
              {{ i18n.t('marketplace.general.transport') }}
            </div>
            <div>
              <input type="checkbox" class="jt-checkbox-v2" v-model="with_transport" id="delivery-checkbox" />
              <label for="delivery-checkbox" class="jt-checkbox-v2"></label>
            </div>
          </div>
          <div v-if="with_transport" class="mb-6 flex items-start gap-4">
            <div class="grow">
              <div class="text-xs mb-2">
                {{ item.attributes.transport.description }}
                {{ item.attributes.transport.area }}
                {{ item.attributes.transport.price }}
              </div>
            </div>
          </div>
        </div>
        <div class="mb-10 flex items-end justify-between">
          <div class="font-bold text-lg top-1 relative">
            {{ i18n.t('marketplace.items_show.price') }}
          </div>
          <div v-if="selectedVariant" class=" font-bold">
            <template v-if="loadingPrice">
              <span class="text-sm opacity-50">{{ i18n.t('marketplace.general.calculating')}}</span>
            </template>
            <template v-else-if="calculatedPrice">
              {{ item.attributes.currency_symbol }}
              <span class="text-2xl">{{ calculatedPrice }}</span>
              <span  v-if="!selectedVariant.tiered_price">{{ item.attributes.price_unit }}</span>
            </template>
            <template v-else>
              <template v-if="selectedVariant.tiered_price">{{ i18n.t('marketplace.general.from') }}</template>
              {{ item.attributes.currency_symbol }}
              <span class="text-2xl">{{ selectedVariant.price }}</span>
              {{ item.attributes.price_unit }}
            </template>
          </div>
        </div>
        <button v-if="!itemAddedToCart" class="jt-btn jt-mk-btn mb-2" :disabled="!isValid" @click="bookItem">
          {{ i18n.t('marketplace.items_show.add_to_cart') }}
        </button>
        <a v-else class="jt-btn btn-secondary jt-mk-btn mb-2" :href="this.cart.urls.checkout">
          {{ i18n.t('marketplace.items_show.view_cart') }}
        </a>
        <div v-if="item.attributes.deposit && !itemAddedToCart" class="text-gray-500 text-sm w-full justify-center flex items-center">
          {{item.attributes.currency_symbol}}{{item.attributes.deposit}} {{ i18n.t('marketplace.items_show.refundable_security_deposit') }}
        </div>
      </div>
      <template v-if="itemAddedToCart" class="w-full">
        <Accessories v-if="item" :item="item" :cart="cart"/>
      </template>
    </div>

  </div>
</template>
<script>

import ItemCalendar from '../../shared/booking_period_picker.vue'
import Accessories from './accessories.vue'
import axios from 'axios';

export default {
  name: 'ItemShowContainer',
  components: {
    ItemCalendar,
    Accessories
  },
  props: {
    itemUrl: String,
    referral: String,
    reviewsUrl: String,
    hasReviews: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      reviews: [],
      item: null,
      activeImage: 0,
      activeTab: 'description',
      cartExists: false,
      itemAddedToCart: false,
      selectedVariant: null,
      showTranslation: false,
      descriptionTranslation: null,
      showReviews: false,
      start_date: null,
      end_date: null,
      reviewsNotLoaded: true,
      cart: null,
      calculatedPrice: null,
      loadingPrice: false,
    }
  },
  watch: {
    showTranslation() {
      if (this.showTranslation) {
        if (!this.descriptionTranslation) {
          fetch(this.selectedVariant.translated_description_url)
            .then(response => response.json())
            .then(data => {
              this.descriptionTranslation = data.description
            })
        }
      }
    },
    showReviews() {
      if (this.showReviews && this.reviewsNotLoaded) {
        this.fetchReviews()
      }
    },
    selectedVariant() {
      this.descriptionTranslation = null;
      this.showTranslation = false;
      // Recalculate price when variant changes if dates are already selected
      if (this.start_date) {
        this.fetchPrice()
      }
    }
  },
  mounted() {
    this.getItem();
    if (this.referral) {
      window.referralCode = this.referral;
      localStorage.setItem('referralCode', this.referral);
    }
    window.scrollTo(0, 0);
  },
  created() {
    this.checkCartState();
  },
  computed: {
    isValid() {
      return this.cart || (this.start_date && this.end_date) || (this.isLesson && this.start_date);
    },
    isLesson() {
      return this.item.attributes.itype === 1;
    },
    variants() {
      if (!this.item) return [];
      return this.item.attributes.variants
    },
    activeItem() {
      if (!this.item) return null;
      return this.selectedVariant || this.item.attributes
    }
  },
  methods: {
    getItem() {
      fetch(this.itemUrl)
        .then(response => response.json())
        .then(data => {
          console.log(data.data)
          this.item = data.data
          this.selectedVariant = this.item.attributes.variants[0]
        })
    },
    fetchReviews() {
      fetch(this.reviewsUrl)
        .then(response => response.json())
        .then(data => {
          this.reviews = data
          this.reviewsNotLoaded = false
        })
    },
    updateDates(period) {
      this.start_date = period.from
      this.end_date = period.to
      if (!period.from) {
        this.calculatedPrice = null
        return
      }
      this.fetchPrice()
    },
    fetchPrice() {
      if (!this.selectedVariant || !this.start_date) {
        return
      }

      // For lessons, we only need start_date
      if (this.isLesson && !this.start_date) {
        return
      }

      // For rentals, we need both dates
      if (!this.isLesson && (!this.start_date || !this.end_date)) {
        return
      }

      this.loadingPrice = true

      const params = {
        item_id: this.selectedVariant.id,
        start_date: this.start_date,
        end_date: this.end_date
      }

      axios.get('/marketplace/calculate_price', { params })
        .then(response => {
          this.calculatedPrice = response.data.price
          this.loadingPrice = false
        })
        .catch(error => {
          console.error('Error calculating price:', error)
          this.loadingPrice = false
          this.calculatedPrice = null
        })
    },
    moveNextImage() {
      this.activeImage += 1
      if (this.activeImage > this.item.attributes.images.length - 1) {
        this.activeImage = 0
      }
    },
    movePreviousImage() {
      this.activeImage -= 1
      if (this.activeImage < 0) {
        this.activeImage = this.item.attributes.images.length - 1
      }
    },
    bookItem() {
      if (!this.cart) {
        window.topNavigation?.$refs.topNavigation.updateCart(
          this.selectedVariant.id,
          1,
          {
            profile_id: this.item.attributes.profile_id,
            start_date: this.start_date,
            end_date: this.end_date
          }
        )
        window.scrollTo(0, 0);
      } else {
        window.topNavigation?.$refs.topNavigation.updateCart(
          this.selectedVariant.id,
          1
        )
      }
    },
    checkCartState() {
      if (window.topNavigation?.$refs.topNavigation?.cart) {
        this.cartExists = true
        this.cart = window.topNavigation.$refs.topNavigation.cart.attributes
        if (this.item) {
          this.itemAddedToCart = window.topNavigation?.$refs.topNavigation.cartItems.find(item => item.attributes.item_id == this.selectedVariant?.id)
        }
      } else {
        this.cartExists = false
        this.itemAddedToCart = false
        this.cart = null
      }
      setTimeout(() => {
       this.checkCartState()
      }, 300)
    }
  }
}
</script>
